import {
  PRODUCT_CATEGORIES,
  PRODUCT_CATEGORY_LABELS,
} from "@artisancode/api-types";
import { Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { SearchableSelect } from "@/components/shared/searchable-select";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { NumberInput } from "@/components/ui/number-input";
import { useProducts } from "@/hooks/use-products";
import { useUpdateWorkspaceBonusRules } from "@/hooks/use-workspaces";
import { formatCurrency } from "@/lib/utils";

import { DailyWageRateCard } from "./daily-wage-rate-card";

import type {
  CategoryBonusRule,
  ProductCategory,
  UpsertCategoryBonusRuleReq,
  Workspace,
} from "@artisancode/api-types";

interface CategoryFormState {
  thresholdQty: number;
  ratePerUnit: number;
  baseRatePerUnit: number;
}

interface ProductRuleFormState {
  key: number;
  productId: string;
  thresholdQty: number;
  ratePerUnit: number;
  baseRatePerUnit: number;
}

export function BonusRulesForm({
  workspace,
  initialRules,
  onSaved,
}: {
  workspace: Workspace;
  initialRules?: CategoryBonusRule[];
  onSaved: () => void;
}) {
  const { mutate: updateRules, isPending } = useUpdateWorkspaceBonusRules();
  const { data: productList } = useProducts({ per_page: 100 });
  const products = productList?.items ?? [];

  const [formState, setFormState] = useState<
    Record<ProductCategory, CategoryFormState>
  >(() => {
    const init = {} as Record<ProductCategory, CategoryFormState>;
    for (const cat of PRODUCT_CATEGORIES) {
      const match = initialRules?.find(
        (r) => !r.productId && r.category === cat,
      );
      init[cat] = {
        thresholdQty: match ? match.thresholdQty : 0,
        ratePerUnit: match ? match.ratePerUnit : 0,
        baseRatePerUnit: match ? match.baseRatePerUnit : 0,
      };
    }
    return init;
  });

  const [productRules, setProductRules] = useState<ProductRuleFormState[]>(() =>
    (initialRules ?? [])
      .filter((r) => r.productId)
      .map((r, i) => ({
        key: i,
        productId: r.productId as string,
        thresholdQty: r.thresholdQty,
        ratePerUnit: r.ratePerUnit,
        baseRatePerUnit: r.baseRatePerUnit,
      })),
  );
  const [nextKey, setNextKey] = useState(productRules.length);

  function handleChange(
    category: ProductCategory,
    field: "thresholdQty" | "ratePerUnit" | "baseRatePerUnit",
    value: number,
  ) {
    setFormState((prev) => ({
      ...prev,
      [category]: {
        ...prev[category],
        [field]: value,
      },
    }));
  }

  function addProductRule() {
    setProductRules((prev) => [
      ...prev,
      {
        key: nextKey,
        productId: "",
        thresholdQty: 0,
        ratePerUnit: 0,
        baseRatePerUnit: 0,
      },
    ]);
    setNextKey((k) => k + 1);
  }

  function removeProductRule(key: number) {
    setProductRules((prev) => prev.filter((r) => r.key !== key));
  }

  function handleProductRuleChange(
    key: number,
    field: "productId" | "thresholdQty" | "ratePerUnit" | "baseRatePerUnit",
    value: string | number,
  ) {
    setProductRules((prev) =>
      prev.map((r) => (r.key === key ? { ...r, [field]: value } : r)),
    );
  }

  const productIdCounts = productRules.reduce<Record<string, number>>(
    (acc, r) => {
      if (r.productId) acc[r.productId] = (acc[r.productId] ?? 0) + 1;
      return acc;
    },
    {},
  );
  const isDuplicateProduct = (productId: string) =>
    productId !== "" && (productIdCounts[productId] ?? 0) > 1;
  const hasDuplicateProduct = Object.values(productIdCounts).some(
    (count) => count > 1,
  );

  function handleSave() {
    if (hasDuplicateProduct) {
      toast.error(
        "Ada produk yang dipilih lebih dari sekali di Override per Produk.",
      );
      return;
    }

    const categoryPayload: UpsertCategoryBonusRuleReq[] =
      PRODUCT_CATEGORIES.map((cat) => ({
        category: cat,
        thresholdQty: Math.max(0, formState[cat]?.thresholdQty ?? 0),
        ratePerUnit: Math.max(0, formState[cat]?.ratePerUnit ?? 0),
        baseRatePerUnit: Math.max(0, formState[cat]?.baseRatePerUnit ?? 0),
      }));

    const productPayload: UpsertCategoryBonusRuleReq[] = productRules
      .filter((r) => r.productId)
      .map((r) => {
        const product = products.find((p) => p.id === r.productId);
        return {
          category: product?.category ?? "other",
          productId: r.productId,
          thresholdQty: Math.max(0, r.thresholdQty),
          ratePerUnit: Math.max(0, r.ratePerUnit),
          baseRatePerUnit: Math.max(0, r.baseRatePerUnit),
        };
      });

    const payload = [...categoryPayload, ...productPayload];

    updateRules(
      { workspaceId: workspace.id, rules: payload },
      {
        onSuccess: () => {
          toast.success("Aturan bonus rider berhasil diperbarui.");
          onSaved();
        },
        onError: () => {
          toast.error("Gagal menyimpan aturan bonus rider.");
        },
      },
    );
  }

  return (
    <div className="space-y-4">
      <DailyWageRateCard workspace={workspace} />

      <div className="grid gap-4">
        {PRODUCT_CATEGORIES.map((cat) => {
          const rule = formState[cat] ?? { thresholdQty: 0, ratePerUnit: 0 };
          return (
            <Card key={cat} className="bg-muted/20">
              <CardContent className="space-y-3 pt-6">
                <div className="flex items-center justify-between border-b pb-2">
                  <span className="text-sm font-semibold">
                    {PRODUCT_CATEGORY_LABELS[cat]}
                  </span>
                  <span className="font-mono text-xs text-muted-foreground">
                    {cat}
                  </span>
                </div>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs">
                      Tarif Pendapatan Penjualan (Rp/unit)
                    </Label>
                    <NumberInput
                      placeholder="0"
                      value={rule.baseRatePerUnit}
                      onChange={(value) =>
                        handleChange(cat, "baseRatePerUnit", value)
                      }
                    />
                    <p className="text-[11px] text-muted-foreground">
                      {rule.baseRatePerUnit > 0
                        ? `${formatCurrency(rule.baseRatePerUnit)} per unit terjual, dibayar ke rider di Penggajian.`
                        : "Tidak ada komisi penjualan per unit untuk kategori ini."}
                    </p>
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs">
                      Threshold Qty (Batas Minimum)
                    </Label>
                    <NumberInput
                      placeholder="0"
                      value={rule.thresholdQty}
                      onChange={(value) =>
                        handleChange(cat, "thresholdQty", value)
                      }
                    />
                    <p className="text-[11px] text-muted-foreground">
                      {rule.thresholdQty > 0
                        ? `Bonus aktif jika penjualan > ${rule.thresholdQty} unit.`
                        : "Bonus berlaku dari unit pertama (tanpa target minimum)."}
                    </p>
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs">Tarif Bonus per Unit (Rp)</Label>
                    <NumberInput
                      placeholder="0"
                      value={rule.ratePerUnit}
                      onChange={(value) =>
                        handleChange(cat, "ratePerUnit", value)
                      }
                    />
                    <p className="text-[11px] text-muted-foreground">
                      {rule.ratePerUnit > 0
                        ? `${formatCurrency(rule.ratePerUnit)} per unit terjual yang memenuhi syarat.`
                        : "Tidak ada bonus per unit untuk kategori ini."}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card>
        <CardContent className="space-y-3 pt-6">
          <div className="flex items-center justify-between border-b pb-2">
            <div>
              <p className="text-sm font-semibold">Override per Produk</p>
              <p className="text-[11px] text-muted-foreground">
                Aturan khusus ini menggantikan aturan kategori untuk produk yang
                dipilih.
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addProductRule}
            >
              <Plus className="h-3.5 w-3.5" />
              Tambah Override
            </Button>
          </div>

          {productRules.length === 0 ? (
            <p className="py-1 text-xs text-muted-foreground">
              Belum ada override khusus produk.
            </p>
          ) : (
            <div className="space-y-2">
              {productRules.map((rule) => (
                <div
                  key={rule.key}
                  className="grid grid-cols-1 items-start gap-2 rounded-lg border bg-muted/20 p-3 sm:grid-cols-[2fr_1fr_1fr_1fr_auto]"
                >
                  <div className="space-y-1">
                    <Label className="text-xs">Produk</Label>
                    <SearchableSelect
                      value={rule.productId}
                      onChange={(v) =>
                        handleProductRuleChange(rule.key, "productId", v)
                      }
                      placeholder="Pilih produk..."
                      options={products.map((p) => ({
                        value: p.id,
                        label: p.name,
                        hint: PRODUCT_CATEGORY_LABELS[p.category],
                      }))}
                    />
                    {isDuplicateProduct(rule.productId) && (
                      <p className="text-[11px] text-destructive">
                        Produk ini sudah dipilih di baris lain.
                      </p>
                    )}
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Pendapatan/Unit (Rp)</Label>
                    <NumberInput
                      placeholder="0"
                      value={rule.baseRatePerUnit}
                      onChange={(value) =>
                        handleProductRuleChange(
                          rule.key,
                          "baseRatePerUnit",
                          value,
                        )
                      }
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Threshold Qty</Label>
                    <NumberInput
                      placeholder="0"
                      value={rule.thresholdQty}
                      onChange={(value) =>
                        handleProductRuleChange(rule.key, "thresholdQty", value)
                      }
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Tarif Bonus/Unit (Rp)</Label>
                    <NumberInput
                      placeholder="0"
                      value={rule.ratePerUnit}
                      onChange={(value) =>
                        handleProductRuleChange(rule.key, "ratePerUnit", value)
                      }
                    />
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="mt-5 shrink-0"
                    onClick={() => removeProductRule(rule.key)}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex justify-end gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={onSaved}
          disabled={isPending}
        >
          Batal
        </Button>
        <Button type="button" onClick={handleSave} disabled={isPending}>
          {isPending ? "Menyimpan..." : "Simpan Perubahan"}
        </Button>
      </div>
    </div>
  );
}
